import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MessagingApi } from '@/api';
import { AuthContext } from '@/context/auth/auth.context';
import { StringUtils } from '@/utils';

export interface INotificationAttachment {
    file_id?: string;
    fileName?: string;
    filePath?: string;
}

export interface INotificationItem {
    id: number;
    title: string;
    body: string;
    messageType: string;
    createdAt: string;
    seenAt: string | null;
    attachments: INotificationAttachment[];
}

const CACHE_PREFIX = 'notification_archive_';
const CACHE_LIMIT = 30;

const mapMessageToNotification = (row: any): INotificationItem => ({
    id: row.id,
    title: row.title ?? '',
    body: row.body ?? '',
    messageType: row.notification_type,
    createdAt: row.received_at,
    seenAt: null,
    attachments: Array.isArray(row.attachments) ? row.attachments : [],
});

const readCache = (key: string): INotificationItem[] => {
    try {
        const raw = localStorage.getItem(CACHE_PREFIX + key);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

const writeCache = (key: string, items: INotificationItem[]): void => {
    try {
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(items.slice(0, CACHE_LIMIT)));
    } catch {
        // storage unavailable (private mode / quota) — archive simply won't persist across reloads
    }
};

const initialState = {
    activeNotification: null as INotificationItem | null,
    bellNotifications: [] as INotificationItem[],
    bellUnseenCount: 0,
    openNotification: (_item: INotificationItem): void => undefined,
    closeNotification: (): void => undefined,
    markAllSeen: (): void => undefined,
};

export const NotificationContext = createContext<any>(initialState);

export const NotificationProvider = ({ children }) => {
    const { userId, isAuthenticated } = useContext(AuthContext);
    const [queue, setQueue] = useState<INotificationItem[]>([]);
    const [archive, setArchive] = useState<INotificationItem[]>([]);
    const [manuallyOpened, setManuallyOpened] = useState<INotificationItem | null>(null);
    const fetchedForRef = useRef<string | null>(null);

    const sessionId = StringUtils.getVisitorId();
    const viewerKey = userId ? `user_${userId}` : `visitor_${sessionId}`;

    useEffect(() => {
        if (isAuthenticated === null || fetchedForRef.current === viewerKey) {
            return;
        }
        fetchedForRef.current = viewerKey;

        setArchive(readCache(viewerKey));

        const fetchRows = isAuthenticated
            ? MessagingApi.my({
                '$select': '',
                '$search': '',
                '$filter': '1 eq 1',
                '$queryParams': { is_read: 0 },
                '$expand': '',
                '$orderby': 'created_at desc',
                '$top': 20,
                '$skip': 0,
            }).then((res: any) => res.data?.data?.results ?? [])
            : MessagingApi.publicAnnouncements().then((res: any) => res.data?.data ?? []);

        fetchRows
            .then((rows: any[]) => {
                // .my() merges targeted (IN_APP) rows in with announcement rows — only
                // announcements pop up; IN_APP messages are inbox-only.
                const announcements = rows.filter((row: any) => !row.source || row.source === 'announcement');
                const cachedIds = new Set(readCache(viewerKey).map((item) => item.id));
                setQueue(announcements.map(mapMessageToNotification).filter((item) => !cachedIds.has(item.id)));
            })
            .catch(() => {
                setQueue([]);
            });
    }, [isAuthenticated, userId, viewerKey]);

    // Only set when reopened from the bell — the automatic popup shows the whole
    // unseen queue at once (see unseenQueue below), not a single front-of-queue item.
    const activeNotification = manuallyOpened;

    const openNotification = useCallback((item: INotificationItem) => {
        setManuallyOpened(item);
    }, []);

    // Marks a batch of items seen together and archives them.
    const settleSeen = useCallback((items: INotificationItem[]) => {
        if (items.length === 0) {
            return;
        }
        if (isAuthenticated) {
            items.forEach((item) => MessagingApi.inboxRead(item.id).catch(() => undefined));
        }

        const seenAt = new Date().toISOString();
        const seenItems = items.map((item) => ({ ...item, seenAt }));
        setArchive((prev) => {
            const seenIds = new Set(seenItems.map((item) => item.id));
            const next = [...seenItems, ...prev.filter((item) => !seenIds.has(item.id))];
            writeCache(viewerKey, next);
            return next;
        });
    }, [viewerKey, isAuthenticated]);

    // Reopened from the bell (manuallyOpened set): only that one item settles.
    // Automatic popup (manuallyOpened null): every queued item settles together,
    // matching the single combined modal that displays them all at once.
    const closeNotification = useCallback(() => {
        if (manuallyOpened) {
            const current = manuallyOpened;
            if (queue.some((item) => item.id === current.id)) {
                settleSeen([current]);
                setQueue((prev) => prev.filter((item) => item.id !== current.id));
            }
            setManuallyOpened(null);
            return;
        }

        if (queue.length > 0) {
            settleSeen(queue);
            setQueue([]);
        }
    }, [manuallyOpened, queue, settleSeen]);

    // Settles the queue without opening anything — the bell's "Mark all as read",
    // as opposed to closeNotification() which settles as a side effect of dismissing
    // the popup.
    const markAllSeen = useCallback(() => {
        if (queue.length === 0) {
            return;
        }
        settleSeen(queue);
        setQueue([]);
    }, [queue, settleSeen]);

    const bellNotifications = [...queue, ...archive].sort((a, b) => b.id - a.id);

    const contextValue = {
        activeNotification,
        unseenQueue: queue,
        bellNotifications,
        bellUnseenCount: queue.length,
        openNotification,
        closeNotification,
        markAllSeen,
    };

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationProvider;
